import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { RovoIcon } from '@atlaskit/legacy-custom-icons';
import { Box, Inline, Pressable, xcss } from '@atlaskit/primitives';
import { usePublish } from '@atlaskit/rovo-triggers';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onOpenChatButtonClick } from '../../../ai-answer-dialog/analytics';
import { useAnalytics } from '../../controller';
import { ErrorBoundary } from '../error-boundary';

import { messages } from './messages';

type OpenChatButtonProps = {
	prompt: string;
	query: string;
	answerString: string;
	source: 'reading-aids' | 'sain';
};

const buttonStyles = xcss({
	color: 'color.text',
	padding: 'space.050',
	paddingInlineEnd: 'space.100',
	borderRadius: 'border.radius.200',
	font: token('font.body'),
	fontWeight: token('font.weight.medium', '500'),
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	flexShrink: 1,
	backgroundColor: 'color.background.neutral',
	width: 'auto',

	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
	},

	':active': {
		backgroundColor: 'color.background.neutral.pressed',
	},
});

const buttonInlineStyles = xcss({
	wordBreak: 'break-word',
	textAlign: 'left',
	whiteSpace: 'pre-wrap',
	alignItems: 'center',
});

export const OpenChatButton = ({ prompt, answerString, query, source }: OpenChatButtonProps) => {
	const { commonAttributes, onClose, readingAidsOptions } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const publish = usePublish('ai-mate');
	const { formatMessage } = useIntl();

	const onOpenChatClick = useCallback(() => {
		fireAnalyticsEvent(onOpenChatButtonClick({ ...commonAttributes }));

		// callback is shared with followups
		// used in the issue model view to open up chat in a new tab
		// this is because we don't have access to dismiss the issue view modal
		if (readingAidsOptions?.onFollowUpCallback) {
			readingAidsOptions.onFollowUpCallback(prompt);
			return;
		}

		publish({
			type: 'chat-new',
			source,
			data: {
				name: query.substring(0, 30),
				dialogues: [
					{
						human_message: {
							content: prompt,
						},
						agent_message: { content: answerString },
					},
				],
			},
		});

		onClose?.();
	}, [
		query,
		answerString,
		publish,
		onClose,
		commonAttributes,
		fireAnalyticsEvent,
		prompt,
		source,
		readingAidsOptions,
	]);

	return (
		<ErrorBoundary fallback={<></>}>
			<Pressable xcss={buttonStyles} onClick={onOpenChatClick}>
				<Inline xcss={buttonInlineStyles}>
					<RovoIcon label="" primaryColor={token('color.icon.subtle')} />
					<Box xcss={[buttonInlineStyles]}>
						{formatMessage(messages.ai_answer_component_ai_answer_open_chat)}
					</Box>
				</Inline>
			</Pressable>
		</ErrorBoundary>
	);
};
